// src/util.ts
var isSSR = () => typeof window === "undefined";
var dateNow = () => /* @__PURE__ */ new Date();
var now = () => +dateNow();
var date = (value) => new Date(value);
var isoDate = () => dateNow().toISOString();
var utcDate = (value) => date(value).toUTCString();
var toSeconds = (ms) => Math.round(ms / 1e3);
var flattenObject = (obj = {}, prefix) => objectKeys(obj).reduce((acc, k) => {
  const pre = prefix?.length ? `${prefix}.` : "";
  const value = obj[k];
  if (isArray(value)) {
    acc[pre + k] = value;
  } else if (isObject(value) && objectKeys(value || {}).length > 0) {
    Object.assign(acc, flattenObject(value, pre + k));
  } else {
    acc[pre + k] = value;
  }
  return acc;
}, {});
var ipRangeCheck = (ip, range) => {
  const [rangeAddress, rangePrefix] = range.split("/");
  const ipBits = ip.split(".").map((num) => parseInt(num, 10).toString(2).padStart(8, "0")).join("");
  const rangeBits = rangeAddress.split(".").map((num) => parseInt(num, 10).toString(2).padStart(8, "0")).join("");
  const prefix = parseInt(rangePrefix, 10);
  return ipBits.substring(0, prefix) === rangeBits.substring(0, prefix);
};
var tryParse = (stringified) => {
  try {
    return JSON.parse(stringified || "");
  } catch (e) {
    return stringified;
  }
};
var objectMatches = (obj, source) => isObject(obj) && isObject(source) && Object.keys(source).every(
  (key) => (
    // eslint-disable-next-line no-prototype-builtins
    obj.hasOwnProperty(key) && obj[key] === source[key]
  )
  // Object.prototype.hasOwnProperty.call(obj, key) && obj[key] === source[key]
);
var trimToLower = (val) => `${val}`.trim().toLowerCase();
var isArray = (arr) => Array.isArray(arr);
var isNullOrUndefined = (val) => val === null || typeof val === "undefined";
var isNumber = (val) => typeof val === "number";
var isObject = (val) => !!val && typeof val === "object";
var isStore = (store) => isObject(store) && "cpid" in store;
var isString = (val) => typeof val === "string";
var isStringArray = (array) => isArray(array) && array.every((element) => typeof element === "string");
var isUndefined = (val) => typeof val === "undefined";
var isManifestClient = (client) => isObject(client) && "get" in client;
var objectKeys = Object.keys;
var objectFromEntries = Object.fromEntries;
var objectFromEntriesPreserveMultiples = (entries) => {
  const obj = {};
  for (const [param, value] of entries) {
    if (isArray(obj[param]))
      obj[param].push(...isArray(value) ? value : [value]);
    else if (!isUndefined(obj[param]))
      obj[param] = [obj[param], ...isArray(value) ? value : [value]];
    else obj[param] = value;
  }
  return obj;
};
var stringify = JSON.stringify;
var stringifyReplacer = (key, value) => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: value.cause,
      t: now()
    };
  }
  return value;
};
var isObjectContentEqual = (a, b) => a && b && stringify(a) === stringify(b);

// src/audiences.ts
var CalculateAudiences = class {
  computed = [];
  matched = [];
  t = now();
  #context;
  constructor(context) {
    this.#context = context;
    if (isSSR()) return;
    const { computed, matched: matches } = this;
    for (const audience of context.manifest?.audiences || []) {
      const matched = this.#check(audience.conditions);
      const a = { ...audience, matched };
      if (matched) matches.push(a);
      computed.push(a);
    }
    let matchLen = matches.length;
    let prevLen = 0;
    while (matchLen > prevLen) {
      prevLen = matchLen;
      for (const audience of computed.filter((a) => !a.matched)) {
        const matched = this.#check(audience.conditions);
        if (matched) {
          audience.matched = true;
          matches.push(audience);
          ++matchLen;
        }
      }
    }
    if (matchLen)
      context.l(
        "am",
        matchLen,
        matches.map((m) => m.id)
      );
  }
  get active() {
    return this.state.active;
  }
  /** Return the state of the audiences, checking for newly matched audiences we may not have detected earlier */
  get state() {
    const { computed, matched: matches, t } = this;
    const { debug, manifest, page: p, state } = this.#context;
    const matchedPrev = state.audiences?.matched || {};
    const allIds = /* @__PURE__ */ new Set([
      ...matches.map((m) => m.id),
      ...objectKeys(matchedPrev)
    ]);
    const matched = {};
    for (const audienceId of allIds) {
      if (matches.find((m) => m.id === audienceId)) {
        matched[audienceId] = [
          { p, t }
          // // Add prev match(es)
          // ...(matchedPrev[audienceId] || []),
        ];
      } else {
        if (manifest?.audiences.find((a) => a.id === audienceId))
          matched[audienceId] = matchedPrev[audienceId];
      }
    }
    const active = objectKeys(matched);
    const nextState = {
      computed: debug && computed.length ? objectFromEntries(
        computed.map((a) => [a.id, [{ p, t, m: a.matched }]])
      ) : void 0,
      matched,
      active
    };
    return nextState;
  }
  /**
   * An audience will contain a collection of conditions wrapped
   * in a single and/or array. Iterate criteria and evaluate
   * each to produce a final boolean match
   */
  #check = (conditions) => {
    if ("and" in conditions && conditions.and.length) {
      for (const and of conditions.and) {
        if ("and" in and || "or" in and) {
          const match = this.#check(and);
          if (!match) return false;
        } else if ("not" in and) {
          const match = this.#evaluate(and.not);
          if (match) return false;
        } else {
          const match = this.#evaluate(and);
          if (!match) return false;
        }
      }
      return true;
    }
    if ("or" in conditions) {
      for (const or of conditions.or) {
        if ("and" in or || "or" in or) {
          const match = this.#check(or);
          if (match) return true;
        } else if ("not" in or) {
          const notMatch = !this.#evaluate(or.not);
          if (!notMatch) return true;
        } else {
          const match = this.#evaluate(or);
          if (match) return true;
        }
      }
      return false;
    }
    return false;
  };
  /** Evaluate one audience condition and return a boolean match */
  #evaluate = (condition) => {
    if (condition.type === "audience") {
      const match = !!this.active.find(
        (audienceId) => audienceId === condition.id
      );
      return match;
    } else if (condition.type === "signal") {
      const match = !!this.#context.state.signals?.active.find(
        (signalId) => signalId === condition.id
      );
      return match;
    }
    return false;
  };
};

// src/signals/evaluate.ts
var CONTAINS = "contains";
var EQUAL_TO = "equalTo";
var IN = "in";
var EXISTS = "exists";
var GREATER_THAN = "greaterThan";
var LESS_THAN = "lessThan";
var MATCHES_REGEX = "matchesRegex";
var STARTS_WITH = "startsWith";
var ENDS_WITH = "endsWith";
var GREATER_THAN_OR_EQUAL = "greaterThanOrEqualTo";
var LESS_THAN_OR_EQUAL = "lessThanOrEqualTo";
var BETWEEN = "between";
var IN_RANGE = "inRange";
var EvaluateSignal = (criteria, value) => {
  const process = (value2) => {
    let match = false;
    if (Array.isArray(value2)) {
      for (const item of value2) {
        match = evaluate(item);
        if (match) break;
      }
    } else {
      match = evaluate(value2);
    }
    return match;
  };
  const evaluate = (value2) => {
    if (BETWEEN in criteria && (isString(value2) || isNumber(value2)))
      return value2 >= criteria[BETWEEN][0] && value2 <= criteria[BETWEEN][1];
    if (CONTAINS in criteria && isString(value2))
      return value2.includes(criteria[CONTAINS]);
    if (EQUAL_TO in criteria && !isUndefined(value2))
      if (isString(value2))
        return trimToLower(value2) === trimToLower(criteria[EQUAL_TO]);
      else return value2 == criteria[EQUAL_TO];
    if (IN in criteria && isArray(criteria[IN]) && !isNullOrUndefined(value2))
      return criteria[IN].map((ic) => trimToLower(ic)).includes(
        trimToLower(value2)
      );
    if (IN_RANGE in criteria && isString(value2))
      return ipRangeCheck(value2, criteria[IN_RANGE]);
    if (EXISTS in criteria) return !isNullOrUndefined(value2);
    if (GREATER_THAN in criteria && isNumber(value2))
      return value2 > criteria[GREATER_THAN];
    if (GREATER_THAN_OR_EQUAL in criteria && isNumber(value2))
      return value2 >= criteria[GREATER_THAN_OR_EQUAL];
    if (LESS_THAN in criteria && isNumber(value2))
      return value2 < criteria[LESS_THAN];
    if (LESS_THAN_OR_EQUAL in criteria && isNumber(value2))
      return value2 <= criteria[LESS_THAN_OR_EQUAL];
    if (MATCHES_REGEX in criteria && !isUndefined(value2))
      return new RegExp(criteria[MATCHES_REGEX]).test(`${value2}`);
    if (STARTS_WITH in criteria && isString(value2))
      return trimToLower(value2).startsWith(trimToLower(criteria[STARTS_WITH]));
    if (ENDS_WITH in criteria && isString(value2))
      return trimToLower(value2).endsWith(trimToLower(criteria[ENDS_WITH]));
    return false;
  };
  return process(value);
};

// src/providers/store.ts
var w = isSSR() ? void 0 : window;
var cookie = (set) => {
  if (isString(set)) document.cookie = set;
  return document.cookie;
};
var Store = class {
  // type: "localStorage" | "sessionStorage" | "cookie" = "localStorage";
  type = "localStorage";
  key = "cp";
  #persist = true;
  /**
   * Store is an interface for persisting values or (stringifiable) objects for Personalization
   * Provide a key to maintain a particular store or omit the key to write to the default "cp" store
   * The store type can be set and used for sessionStorage or a cookie, localStorage is default
   * Use sessionStorage or localStorage if persist is false or true (default: true)
   */
  constructor({
    key,
    persist = true,
    type = "localStorage"
  }) {
    this.#persist = persist;
    if (key) this.key = key;
    this.type = type === "localStorage" && !persist ? "sessionStorage" : type;
  }
  get = ({
    type = this.type,
    key = this.key
  } = {}) => {
    if (isSSR()) return void 0;
    const stringified = type === "c" ? cookie().split(";").filter((cookie2) => cookie2.trim().startsWith(`${key}=`)).map((cookie2) => cookie2.trim().substring(key.length + 1))?.[0] : w[type].getItem(key);
    if (stringified) {
      return tryParse(stringified);
    }
  };
  getAll = ({ type = this.type } = {}) => {
    if (isSSR()) return void 0;
    if (type === "c")
      return objectFromEntriesPreserveMultiples(
        cookie().split(";").map((cookie2) => cookie2.trim().split("="))
      );
    return w[type];
  };
  getString = ({ type = this.type } = {}) => {
    if (isSSR()) return "";
    return type === "c" ? `${cookie()}` : stringify(w[type]);
  };
  set = (value, { type = this.type, key = this.key } = {}) => {
    if (isSSR()) return void 0;
    const stringified = isObject(value) ? stringify(value, stringifyReplacer) : value;
    if (type === "c") {
      const expires = now() + 30 * 24 * 60 * 60 * 1e3;
      return cookie(
        `${key}=${stringified};${this.#persist ? `expires=${utcDate(expires)};` : ""}SameSite=Lax`
      );
    }
    const maxSize = 1048576;
    if (stringified.length > maxSize) {
      return this.#shrink(
        value,
        stringified.length,
        maxSize
      );
    }
    return w[type].setItem(key, `${stringified}`);
  };
  clear = ({ type = this.type, key = this.key } = {}) => {
    if (isSSR()) return void 0;
    return type === "c" ? cookie(`${key}=;expires=${utcDate(0)}`) : w[type].removeItem(key);
  };
  #shrink = (value, originalSize, targetSize, nuke = false) => {
    value.signals.computed = void 0;
    if (!nuke) {
      if (value.signals.matched)
        for (const [signal, matches] of Object.entries(
          value.signals.matched || {}
        )) {
          value.signals.matched[signal] = matches.slice(0, 9);
        }
    } else {
      value.signals.matched = {};
    }
    const size = stringify(value, stringifyReplacer).length;
    if (size <= targetSize) {
      console.info(
        `[Store] shrunk from ${originalSize} to ${size}/${targetSize}`
      );
      this.set(value);
    } else if (!nuke) {
      this.#shrink(value, size, targetSize, true);
    } else if (nuke) {
      console.error(
        `[Store] failed to shrink below ${targetSize} bytes, shrunk from ${originalSize} to ${size}`,
        value
      );
    }
  };
};
var cookieStore = new Store({ type: "c" });

// src/signals/cookies.ts
var CookieSignals = () => {
  const string = cookieStore.getString() || "";
  const cookies = cookieStore.getAll() || {};
  return {
    cookies,
    get: (name) => cookies[name],
    string
  };
};

// src/signals/url.ts
var UrlSignals = (page) => {
  const url = !isSSR() ? new URL(page) : new URL("ssr://");
  return {
    url,
    href: () => url.href,
    domain: () => url.hostname,
    path: () => url.pathname,
    subdomain: () => {
      const parts = url.hostname.split(".");
      return parts?.[0];
    },
    baseUrl: () => `${url.origin}${url.pathname}`,
    querystring: () => url.search,
    queryParam: (name) => {
      const params = url.searchParams.getAll(name);
      if (params.length)
        if (params.length > 1) return params;
        else return params[0];
      else return;
    },
    queryParams: () => {
      const params = url.searchParams;
      params.sort();
      return objectFromEntriesPreserveMultiples(params.entries());
    }
  };
};

// src/signals/route.ts
var RouteSignalsSnapshot = (url, referrer) => {
  const _cookie = CookieSignals();
  const _url = UrlSignals(url);
  let _referrer;
  if (isObject(referrer)) _referrer = referrer;
  else if (referrer) _referrer = RouteSignalsSnapshot(referrer);
  const attributes = {
    "page.url": _url.href(),
    "page.path": _url.path(),
    "page.querystring": _url.querystring(),
    "page.queryParams": (name) => _url.queryParam(name),
    // "page.queryParams.*": _url.queryParams(),
    "page.domain": _url.domain(),
    "page.subdomain": _url.subdomain(),
    "page.baseUrl": _url.baseUrl(),
    "referrer.url": _referrer?.["page.url"],
    "referrer.path": _referrer?.["page.path"],
    "referrer.querystring": _referrer?.["page.querystring"],
    "referrer.queryParams": (name) => _referrer?.["page.queryParams"](name),
    // "referrer.queryParams.*": _referrer?._url.queryParams(),
    "referrer.domain": _referrer?.["page.domain"],
    "referrer.subdomain": _referrer?.["page.subdomain"],
    "referrer.baseUrl": _referrer?.["page.baseUrl"],
    cookie: _cookie.string,
    "cookie.*": _cookie.cookies
  };
  return attributes;
};

// src/signals/app.ts
var AppSignalsSnapshot = (signals) => {
  const attributes = {
    custom: signals
  };
  return signals ? attributes : {};
};

// src/signals/browser.ts
var BrowserSignalsSnapshot = () => {
  if (isSSR()) return {};
  const attributes = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor,
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    touchSupport: "ontouchstart" in window,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled
  };
  return attributes;
};

// src/signals/index.ts
var CalculateSignals = class {
  computed = [];
  matched = [];
  snapshot;
  t = now();
  #context;
  constructor(context) {
    this.#context = context;
    const { custom, currentPage, pageViews, previousPage } = context;
    const previousSignals = pageViews.length > 1 ? pageViews[pageViews.length - 2]?.[2]?.snapshot : void 0;
    const { browser, location: location2, ...sessionSignals } = context.session.state;
    this.snapshot = {
      ...RouteSignalsSnapshot(currentPage, previousSignals || previousPage),
      ...flattenObject(AppSignalsSnapshot(custom)),
      ...flattenObject(sessionSignals, "session"),
      ...flattenObject(BrowserSignalsSnapshot(), "browser"),
      ...flattenObject(location2, "location")
    };
    delete context.custom;
    if (isSSR()) return;
    this.#calculate();
  }
  get active() {
    return this.state.active;
  }
  get attributes() {
    const overrides = this.#context.state.overrides || {};
    return { ...this.snapshot, ...overrides };
  }
  /** Return the state of the signals, merging newly matched signals with those previously matched */
  get state() {
    const { computed, matched: matchedThis, t } = this;
    const { debug, manifest, page: p, state } = this.#context;
    const matchedPrev = state.signals?.matched || {};
    const allIds = /* @__PURE__ */ new Set([
      ...matchedThis.map((m) => m.id),
      ...objectKeys(matchedPrev)
    ]);
    const matched = {};
    for (const signalId of allIds) {
      const currentMatch = findSignal(signalId, matchedThis);
      if (currentMatch && !matchedPrev[signalId]?.find((ps) => ps.t === t)) {
        matched[signalId] = [
          { p, t },
          // Add prev match(es) (hard-limited to last 99)
          ...(matchedPrev[signalId] || []).slice(0, 98)
        ];
      } else {
        if (findSignal(signalId, manifest?.signals))
          matched[signalId] = matchedPrev[signalId];
      }
    }
    const active = [];
    for (const matchedId in matched) {
      const signalManifest = findSignal(matchedId, manifest?.signals);
      if (signalManifest && (signalManifest.minMatches && matched[matchedId].length >= signalManifest.minMatches || !signalManifest?.minMatches))
        active.push(matchedId);
    }
    const nextState = {
      computed: debug && computed.length ? objectFromEntries(
        computed.map((s) => [
          s.id,
          [{ p, t, m: s.matched, mm: s.minMatches }]
        ])
      ) : void 0,
      matched,
      active
    };
    return nextState;
  }
  /** Iterate computed.signals that are unmatched and check signal criteria for a match */
  redo = () => {
    try {
      const { computed, matched: matches } = this;
      this.snapshot = {
        ...this.snapshot,
        ...flattenObject(AppSignalsSnapshot(this.#context.custom))
      };
      delete this.#context.custom;
      this.t = now();
      for (const signal of computed.filter((s) => !s.matched)) {
        const matched = this.#check(signal.where);
        if (matched) {
          signal.matched = true;
          ++signal.times;
          if (!matches.find((m) => m.id === signal.id))
            matches.push(signal);
        }
      }
      this.#log();
    } catch (ex) {
      this.#log(ex);
    }
    return this;
  };
  /** Iterate manifest.signals and check each signal criteria for a match */
  #calculate = () => {
    try {
      const { manifest, state } = this.#context;
      const signals = manifest?.signals || [];
      for (const signal of signals) {
        const matched = this.#check(signal.where);
        const matchedTimes = state.signals?.matched?.[signal.id]?.length || 0;
        const computed = {
          ...signal,
          matched,
          times: matchedTimes + (matched ? 1 : 0)
        };
        if (matched) this.matched.push(computed);
        this.computed.push(computed);
      }
      this.#log();
    } catch (ex) {
      this.#log(ex);
    }
  };
  /**
   * A signal will contain a collection of criteria wrapped
   * in an and/or array. Iterate criteria and evaluate
   * each to produce a final boolean match
   */
  #check = (criteria) => {
    if ("and" in criteria && criteria.and.length) {
      for (const and of criteria.and) {
        if ("and" in and || "or" in and) {
          const match = this.#check(and);
          if (!match) return false;
        } else if ("not" in and) {
          const match = this.#evaluate(and.not);
          if (match) return false;
        } else {
          const match = this.#evaluate(and);
          if (!match) return false;
        }
      }
      return true;
    }
    if ("or" in criteria) {
      for (const or of criteria.or) {
        if ("and" in or || "or" in or) {
          const match = this.#check(or);
          if (match) return true;
        } else if ("not" in or) {
          const notMatch = !this.#evaluate(or.not);
          if (!notMatch) return true;
        } else {
          const match = this.#evaluate(or);
          if (match) return true;
        }
      }
      return false;
    }
    return false;
  };
  /** Evaluate one where criteria and return a boolean match */
  #evaluate = (criteria) => {
    if (!criteria || !("attribute" in criteria)) return false;
    const [attribute, key] = this.#keyedAttribute(
      criteria.attribute
    );
    const signalValue = this.#getSignal(attribute, key);
    const result = EvaluateSignal(criteria, signalValue);
    return result;
  };
  /** Find the value(s) for a given signal attribute */
  #getSignal = (attribute, key = "") => {
    const { attributes: snapshot } = this;
    if (attribute === "cookie.*") return snapshot[attribute]?.[key];
    if (attribute === "page.queryParams" || attribute === "referrer.queryParams")
      return snapshot[attribute](key);
    return snapshot[attribute];
  };
  /**
   * Some attributes can be contain a named suffix `page.queryParams.q`
   * Look for specific named attributes and split the key name (q) from
   * the attribute (page.queryParams), appending .* to the named-attribute
   * returning a tuple of [attribute, key] so we can provide the key
   * separately when we find the value for that signal attribute
   */
  #keyedAttribute = (attribute) => {
    const slices = attribute.includes(".queryParams.") ? 2 : attribute.startsWith("cookie.") ? 1 : 0;
    const att = slices ? `${attribute.split(".").slice(0, slices).join(".")}${slices === 1 ? ".*" : ""}` : attribute;
    const key = slices ? attribute.split(".").slice(slices).join(".") : "";
    return [att, key];
  };
  #log = (err) => {
    const { matched } = this;
    const { l, manifest } = this.#context;
    const t = now();
    if (err) {
      this.#context.session.update({ error: err });
      l("serr", err);
    }
    l("sc", manifest?.signals.length, t - this.t, manifest?.version.versionNo);
    const matches = matched.length;
    if (matches) {
      l(
        "sm",
        matches,
        matched.map((s) => `${s.id}(${s.times} / ${s.minMatches})`).join(", ")
      );
    }
  };
};

// src/session.ts
var Session = class {
  #context;
  #store;
  /** Preserved page view count from the session state */
  #prev;
  constructor(context) {
    this.#context = context;
    this.#store = new Store({ type: "sessionStorage" });
    const state = this.state;
    this.#prev = state.pageViews;
    this.#save = state;
  }
  /** The total number of page views in the context */
  get pvc() {
    return this.#context.pageViews.length;
  }
  /** Check the store for an existing entry or initialise a new state */
  get state() {
    const existing = this.#store?.get();
    return existing || this.#initialState();
  }
  /** Assign any state to persist the value to storage */
  set #save(state) {
    state.pageViews = this.#prev + this.pvc;
    state.lastActivity = isoDate();
    state.duration = toSeconds(now() - +date(state.startTime));
    this.#store.set(state);
  }
  /** Clear the session store */
  clear = () => this.#store.clear();
  /** Provide a partial state and update the session store */
  update = (patch = {}) => {
    this.#save = { ...this.state, ...patch };
  };
  #initialState = () => {
    const date2 = isoDate();
    const browser = BrowserSignalsSnapshot();
    const { page, state } = this.#context;
    const url = UrlSignals(page);
    const initial = {
      isFirstVisit: state.pageViews < 2,
      duration: 0,
      entryPage: url.path(),
      lastActivity: date2,
      pageViews: this.pvc,
      startTime: date2,
      browser,
      location: state.manifest?.location
    };
    const referrer = !isSSR() ? document.referrer : "";
    if (referrer) {
      const url2 = UrlSignals(referrer);
      initial.referrer = {
        baseUrl: url2.baseUrl(),
        domain: url2.domain(),
        path: url2.path(),
        querystring: url2.querystring(),
        queryParams: url2.queryParams(),
        // "queryParams.*": (name: string) => url.queryParam(name),
        subdomain: url2.subdomain(),
        url: url2.href()
      };
    }
    return initial;
  };
};

// src/personalization.ts
var GLOBAL = "CONTENSIS_PERSONALIZATION";
var PersonalizationContext = class {
  static Store = Store;
  /** Output console.log messaging, `true` or `v`=verbose */
  debug;
  /** `true` if the context is running in preview mode */
  preview;
  /** Contensis Personalization Id */
  cpid;
  /** Random percentile for experiment bucketing */
  percentile;
  /** The current page href we are working with */
  currentPage = "";
  /** The previous page href we are working with */
  previousPage;
  /** Holds details pertaining to the current session */
  session;
  /** The audiences last calculated */
  audiences;
  /** The signals last calculated */
  signals;
  /** The Manifest containing the working rules for this context */
  manifest;
  /**
   * The pageViews we have counted and calculated since the Personalization Context was last instantiated
   * Used for counting pageViews in a SPA and monitored by the MutationObserver created in the observe() method
   */
  pageViews = [];
  /** Holds custom attributes for consumption in the next attribute snapshot for signal calculations */
  custom;
  /** Timestamp this context was last updated */
  t = 0;
  /** Log method which is defined if we have set debug flag */
  log;
  /** Private store object initiated to localStorage */
  #store;
  /** Array of user-supplied event handlers */
  #events = [];
  constructor({
    client,
    debug,
    handlers,
    manifest,
    session,
    preview
  } = {}) {
    const { l } = this;
    this.debug = debug || false;
    for (const h in handlers) {
      const method = h;
      if (typeof handlers[method] === "function") {
        this.addHandler(method, handlers[method]);
      }
    }
    this.currentPage = this.page;
    this.#store = new Store({ persist: !session });
    const state = this.state;
    this.preview = typeof preview !== "undefined" ? preview : !!state.manifest?.client?.preview;
    const cookieId = this.#store.get({ type: "c", key: "cpid" });
    if (cookieId) state.cpid = cookieId;
    const id = this.cpid = state.cpid;
    const pc = this.percentile = state.pc / 100;
    this.#save = state;
    this.#save = [id, { type: "c", key: "cpid" }];
    l("init", id, pc, state.pageViews);
    this.session = new Session(this);
    if (manifest) {
      l("im", manifest);
      this.manifest = new Manifest(
        manifest,
        this.#onManifestReady,
        l,
        state.manifest,
        this.preview
      );
    } else if (client) {
      l("ic");
      this.manifest = new Manifest(
        client,
        this.#onManifestReady,
        l,
        state.manifest,
        this.preview
      );
    } else {
      console.warn(`cp: client or manifest required`);
    }
    this.#init();
  }
  /** Safely return the current location.href */
  get page() {
    if (isSSR()) return "";
    return location.href;
  }
  /** Return an existing entry from the store or initialise a new state */
  get state() {
    const existingpc = this.percentile;
    return this.#store.get() || {
      /** New visitor uuid */
      cpid: this.cpid || crypto.randomUUID(),
      /** New percentile random bucketing to 2 dp */
      pc: existingpc ? existingpc * 100 : Math.floor(Math.random() * 1e4),
      pageViews: 0,
      manifest: this.manifest,
      signals: { active: [] },
      audiences: { active: [] }
    };
  }
  /** Get the current pageView from the pageViews array */
  get #cpv() {
    const { pageViews } = this;
    const len = pageViews.length;
    return len ? pageViews[pageViews.length - 1] : null;
  }
  /** Assign any state or [value, options] to persist the value */
  set #save(state) {
    try {
      if (isStore(state)) {
        this.#store.set(state);
      }
      if (isArray(state) && state.length === 2) {
        const [value, opts] = state;
        this.#store.set(value, opts);
      }
    } catch (ex) {
      this.l("e", ex);
    }
  }
  /**
   * Add a handler with a callback function that will be invoked when the event occurs
   * returns the callback function that can be used to supply the removeHandler argument
   */
  addHandler = (event, callback) => {
    this.#events.push([event, callback]);
    return callback;
  };
  /** Clean up any handlers previously added */
  removeHandler = (key, callback) => {
    this.#events = this.#events.filter(
      (e) => e[0] !== key || e[1] !== callback
    );
  };
  /** Log a debug message */
  l = (message, ...values) => {
    if (this.debug)
      if (this.log) this.log(message, ...values);
      else {
        if (this.debug == "v") console.log("cp", message, values);
        this.#logs(true).then(() => this.l(message, ...values)).catch(() => {
          console.log("cp", message, values);
        });
      }
  };
  /**
   * Compute (or recompute) the signals and audiences from the current page
   * @param pageView optional pageView object to compute signals for (defaults to current pageView)
   */
  compute = (pageView = this.#cpv) => {
    this.session.update();
    const existing = pageView?.[2];
    const signals = this.signals = !existing ? new CalculateSignals(this) : existing.redo();
    this.#save = {
      ...this.state,
      signals: signals.state
    };
    if (pageView) pageView[2] = signals;
    const audiences = this.audiences = new CalculateAudiences(this);
    this.#save = {
      ...this.state,
      audiences: audiences.state
    };
    this.session.update();
    this.t = now();
    this.#handler("onComputed", this);
  };
  /** Register a new page view to compute signals and audiences with */
  pageView = (url = this.page) => {
    const { l, pageViews } = this;
    pageViews.push([url, /* @__PURE__ */ new Date(), null]);
    const state = this.state;
    const referrer = !isSSR() ? document.referrer : void 0;
    let currentPage;
    let previousPage;
    if (!state.currentPage) {
      l("pn", referrer);
      currentPage = state.currentPage = url;
      previousPage = state.previousPage = referrer;
    } else if (state.currentPage !== url) {
      l("pc", referrer);
      previousPage = state.previousPage = referrer || state.currentPage;
      currentPage = state.currentPage = url;
    } else {
      l("ps", referrer);
      currentPage = state.currentPage;
      previousPage = state.previousPage = referrer;
    }
    this.currentPage = currentPage;
    this.previousPage = previousPage;
    state.pageViews++;
    l("pv", state.pageViews, pageViews);
    this.#save = state;
    if (this.manifest?.isReady) {
      this.compute();
    } else l("pm");
    this.#handler("onPageView", this, currentPage, previousPage);
  };
  /**
   * Return any custom attributes provided by the app to the
   * personalization context
   */
  getAttributes = (key) => {
    const allAttributes = this.signals?.attributes || {};
    if (isStringArray(key)) {
      const selectedAttributes = {};
      for (const k of key) {
        if (allAttributes && k in allAttributes)
          selectedAttributes[k] = allAttributes[k];
      }
      return selectedAttributes;
    } else if (typeof key === "string") {
      return allAttributes[key];
    }
    return allAttributes;
  };
  /**
   * Supply custom attributes identified within the app to the
   * personalization context
   */
  setAttributes = (customAttributes) => {
    if (objectKeys(customAttributes).length) {
      const existing = this.custom || {};
      const next = { ...existing, ...customAttributes };
      if (!isObjectContentEqual(existing, next)) {
        this.custom = next;
        this.compute();
      }
    }
  };
  /**
   * Set signal attributes within the app to override the
   * personalization context
   */
  overrideAttributes = (overrideSignals) => {
    if (objectKeys(overrideSignals).length) {
      const { state } = this;
      const existing = state.overrides;
      const next = { ...existing, ...overrideSignals };
      if (!isObjectContentEqual(existing, next)) {
        this.#save = { ...state, overrides: next };
        this.compute();
      }
    }
  };
  /** Toggle an audience on or off for use when previewing sites */
  toggleAudience = (audienceId) => {
    const { state } = this;
    if (state.audiences?.matched) {
      if (state.audiences.active.includes(audienceId)) {
        delete state.audiences.matched[audienceId];
      } else {
        state.audiences.matched[audienceId] = [{ p: "preview", t: 0 }];
      }
      this.#save = state;
      this.compute();
    }
  };
  /**
   * Clear all storage and reset the context to its initial state
   * Supply options to control which elements are reset or leave
   * empty to reset everything
   */
  reset = ({
    audiences,
    signals,
    attributes,
    manifest,
    session,
    store
  } = {
    manifest: true,
    session: true,
    store: true
  }) => {
    const me = this;
    if (attributes || signals || audiences) {
      const state = me.state;
      if (attributes) {
        delete state.overrides;
      }
      if (signals) {
        state.signals = { active: [] };
      }
      if (audiences) state.audiences = { active: [] };
      if (me.#cpv) me.#cpv[2] = null;
      me.#save = state;
      me.compute();
    }
    if (store) {
      me.#store.clear();
    }
    if (session) {
      me.pageViews = [];
      me.session.clear();
      me.session = new Session(me);
    }
    if (store || session) me.pageView();
    if (manifest) {
      if (me.#cpv) me.#cpv[2] = null;
      const { manifest: manifest2 } = me;
      if (manifest2?.client) {
        manifest2.client.preview = me.preview;
        manifest2.isReady = false;
        manifest2.init(me.#onManifestReady);
      }
    }
  };
  /**
   * Compute signals for a provided PageView
   * and debug log any updated signals or audiences
   */
  #computePage = (pageView) => {
    const existingSignals = this.signals?.matched?.length || 0;
    const existingAudiences = this.audiences?.active.length || 0;
    this.compute(pageView);
    const hasNewSignals = this.signals?.matched?.length !== existingSignals;
    if (hasNewSignals) this.l("ms");
    const hasNewAudiences = this.audiences?.active.length !== existingAudiences;
    if (hasNewAudiences) this.l("ma");
  };
  /** Invoke event handler(s) */
  #handler = (key, ...args) => {
    for (const [evt, cb] of this.#events.filter((e) => e[0] === key)) {
      cb(...args);
    }
  };
  /** Initialise the context, or bow out here if we are running in SSR */
  #init = () => {
    if (isSSR()) return;
    this.#observe();
    this.#handler("onInit", this);
  };
  /** Dynamically import logging if we have set debug flag */
  #logs = async (debug) => debug && !this.log ? import("./logs-AU7EDJ6B.mjs").then(({ logger }) => {
    this.log = logger;
  }) : void 0;
  /** Monitor for DOM mutations and subsequent href changes */
  #observe = () => {
    let lastHref = "";
    const observer = new MutationObserver(() => {
      const { currentPage, l, page } = this;
      if (page !== lastHref) {
        if (!lastHref) l("n1", page);
        else l("n2", currentPage, page);
        lastHref = page;
        this.pageView();
        this.#handler("onNavigate", this, page, lastHref);
      }
    });
    const bodyEl = document.querySelector("body");
    observer.observe(bodyEl, {
      childList: true,
      subtree: true
    });
  };
  /** Actions to call when the manifest has been loaded */
  #onManifestReady = (manifest) => {
    const { manifest: { client } = {}, l, pageViews, preview, state } = this;
    const stateLocation = state.manifest?.location;
    const manifestLocation = manifest?.location;
    const updateLocation = !objectMatches(stateLocation, manifestLocation);
    if (updateLocation) {
      this.session.update({ location: manifestLocation });
      l("ml", manifestLocation, stateLocation);
    }
    const stateVersion = state.manifest?.version?.versionNo;
    const manifestVersion = manifest?.version?.versionNo;
    if (updateLocation || manifestVersion && manifestVersion !== stateVersion || manifestVersion === "draft" || !manifestVersion) {
      this.#save = { ...state, manifest };
      l(!client ? "mu" : preview ? "mp" : "mv", manifestVersion, stateVersion);
      for (const pageView of pageViews) this.#computePage(pageView);
    } else {
      const toCheck = pageViews.filter((pv) => pv[2] === null);
      for (const pageView of toCheck) this.#computePage(pageView);
    }
    this.#handler("onManifestReady", this, manifest);
  };
};

// src/signals/location.ts
var extractLocationHeaders = (response, body) => {
  let location2 = {};
  for (const [header, value] of response?.headers || []) {
    if (header === "x-geoip-ip") location2.ip = value;
    if (header === "x-geoip-country-code") location2.country = value;
  }
  if (body && "location" in body) {
    const bl = body.location;
    if (isObject(bl)) location2 = { ...location2, ...bl };
  }
  return location2;
};

// src/providers/manifest-client.ts
var ManifestClient = (alias, rootUrl, projectId = "website", token, preview) => {
  const hostname = rootUrl || `https://cms-${alias}.cloud.contensis.com`;
  const returnArgs = { alias, preview, projectId };
  returnArgs.get = async () => {
    try {
      const bearerToken = token || globalThis[GLOBAL]?.token;
      const uri = `${hostname}/api/${bearerToken ? "management" : "delivery"}/projects/${projectId}/personalization/manifest/${returnArgs.preview && !bearerToken ? "preview" : "current"}`;
      const headers = {
        ["x-alias"]: alias
      };
      if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
      const response = await fetch(uri, {
        headers
      });
      if (response.ok) {
        const body = await response.text();
        const parsed = tryParse(body);
        const location2 = extractLocationHeaders(response, parsed);
        return { ...isObject(parsed) ? parsed : {}, location: location2 };
      }
    } catch (ex) {
      const statusCode = isObject(ex) && "statusCode" in ex ? ex.statusCode : "";
      console.error(
        `Manifest[get]:${statusCode ? ` ${statusCode}` : ""} ${ex}`
      );
    }
  };
  return returnArgs;
};

// src/providers/manifest.ts
var findSignal = (id, signals) => signals?.find((signal) => signal.id === id);
var Manifest = class {
  client;
  audiences = [];
  signals = [];
  location;
  version = {};
  isReady = false;
  constructor(client, onReady, log, state, preview) {
    if (isManifestClient(client)) this.client = client;
    else if ("alias" in client)
      this.client = ManifestClient(
        client.alias,
        client.rootUrl,
        client.projectId,
        client.token,
        preview
      );
    else {
      this.audiences = client.audiences || [];
      this.signals = client.signals || [];
      this.location = client.location || {};
      this.version = client.version || {};
    }
    if (state && this.client) {
      log("m");
      this.audiences = state.audiences || [];
      this.signals = state.signals || [];
      this.location = state.location || {};
      this.version = state.version || {};
      this.isReady = state.isReady;
    }
    this.init(onReady);
  }
  init = async (onReady) => {
    if (this.client) {
      const manifest = await this.client.get();
      if (manifest) {
        this.audiences = manifest.audiences;
        this.signals = manifest.signals;
        this.location = manifest.location;
        this.version = manifest.version;
        this.isReady = true;
        onReady(this);
      }
    } else {
      this.isReady = true;
      onReady(this);
    }
  };
};
export {
  EvaluateSignal,
  GLOBAL,
  Manifest,
  PersonalizationContext,
  Store,
  cookieStore,
  findSignal
};
